import { ApiInstance } from '@/lib/api'
import { extractApiError } from '@/lib/errors'
import { MAX_PER_PAGE, type Paginated } from '@/lib/pagination'
import { buildQuery } from '@/lib/query'

export const LOOKUP_CHUNK_SIZE = MAX_PER_PAGE
export const CUSTOMER_SEARCH_PER_PAGE = MAX_PER_PAGE
export const REFERENCE_SEARCH_PER_PAGE = 50

export interface LookupSource {
  key: string
  path: string
  include?: string
}

export const CUSTOMER_LOOKUP: LookupSource = { key: 'customers', path: 'customers', include: 'phones' }
export const REAL_STATE_LOOKUP: LookupSource = { key: 'real-state', path: 'real-state', include: 'address;characteristics' }
export const OWNER_LOOKUP: LookupSource = { key: 'owners', path: 'real-state/owners' }

export function normalizeIds(ids: Iterable<string | null | undefined>): string[] {
  const unique = new Set<string>()
  for (const id of ids) {
    const trimmed = id?.trim()
    if (trimmed) unique.add(trimmed)
  }
  return [...unique].sort()
}

export function chunkIds(ids: string[], size: number = LOOKUP_CHUNK_SIZE): string[][] {
  const chunks: string[][] = []
  for (let start = 0; start < ids.length; start += size) chunks.push(ids.slice(start, start + size))
  return chunks
}

export const idsQuery = (ids: string[]) => buildQuery([{ field: 'id', values: ids }])

export async function fetchByIds<T extends { id: string }>(source: LookupSource, ids: Iterable<string | null | undefined>): Promise<T[]> {
  const chunks = chunkIds(normalizeIds(ids))
  const pages = await Promise.all(
    chunks.map(chunk =>
      ApiInstance.get<Paginated<T>>(source.path, { params: { query: idsQuery(chunk), include: source.include, perPage: LOOKUP_CHUNK_SIZE } })
    )
  )
  return pages.flatMap(({ data }) => data?.rows ?? [])
}

export const searchTerm = (search?: string | null): string | undefined => search?.trim() || undefined

async function searchIdsIn(path: string, search: string, perPage: number): Promise<string[]> {
  const { data } = await ApiInstance.get<Paginated<{ id: string }>>(path, { params: { search, perPage } })
  return (data?.rows ?? []).map(row => row.id)
}

export async function unionOfSettled(searches: Promise<string[]>[]): Promise<string[]> {
  const results = await Promise.allSettled(searches)
  const fulfilled = results.flatMap(result => (result.status === 'fulfilled' ? [result.value] : []))
  if (fulfilled.length === 0) {
    const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
    if (failure) throw failure.reason
  }
  return normalizeIds(fulfilled.flat())
}

export function searchCustomerIds(search: string): Promise<string[]> {
  return unionOfSettled([searchIdsIn(CUSTOMER_LOOKUP.path, search, CUSTOMER_SEARCH_PER_PAGE)])
}

export function searchReferenceIds(search: string): Promise<string[]> {
  return unionOfSettled([
    searchIdsIn(CUSTOMER_LOOKUP.path, search, REFERENCE_SEARCH_PER_PAGE),
    searchIdsIn(OWNER_LOOKUP.path, search, REFERENCE_SEARCH_PER_PAGE),
    searchIdsIn(REAL_STATE_LOOKUP.path, search, REFERENCE_SEARCH_PER_PAGE)
  ])
}

export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const { status } = extractApiError(error)
  if (status != null && status >= 400 && status < 500) return false
  return failureCount < 2
}
